import {useEffect, useRef} from 'react'
import {useGameStore} from '../store/GameStoreContext'
import {useUIContext} from '../contexts/UIContext'

/**
 * @deprecated Only use until UIMGamePlayerInformation is converted to ReactJS
 */
export const usePortraitPlayerInformationContainerPosition = () => {
    const setPlayerInformationContainerPosition = useGameStore((state) => state.gamePortraitPlayerInformationLayout.setPlayerInformationContainerPosition)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    const retryTimeoutRef = useRef<number | null>(null)
    const {isUserOnTablet} = useUIContext()

    useEffect(() => {
        const getElements = () => {
            const statusContainer = document.querySelector('[data-game-current-status-container]') as HTMLElement | null
            const uiGameContainer = document.getElementById('ui-game') as HTMLElement | null
            return {statusContainer: statusContainer, uiGameContainer: uiGameContainer}
        }

        const clearRetry = () => {
            if(retryTimeoutRef.current != null) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = null
            }
        }

        const scheduleRetry = (callback: () => void, delay: number = 100) => {
            clearRetry()
            retryTimeoutRef.current = window.setTimeout(() => {
                retryTimeoutRef.current = null
                callback()
            }, delay)
        }

        const calculatePosition = (statusContainer: HTMLElement, uiGameContainer: HTMLElement) => {
            const statusRect = statusContainer.getBoundingClientRect()
            const gameRect = uiGameContainer.getBoundingClientRect()
            return {
                x: isUserOnTablet === true ? gameRect.width * 0.75 : 0,
                y: statusRect.bottom - gameRect.top,
            }
        }

        const updatePosition = () => {
            const {statusContainer, uiGameContainer} = getElements()

            if(statusContainer == null || uiGameContainer == null) {
                scheduleRetry(updatePosition)
                return
            }

            clearRetry()
            const position = calculatePosition(statusContainer, uiGameContainer)
            setPlayerInformationContainerPosition(position)
        }

        const setupResizeObserver = () => {
            const {statusContainer} = getElements()
            if(statusContainer == null) {
                scheduleRetry(setupResizeObserver)
                return
            }

            resizeObserverRef.current?.disconnect()
            resizeObserverRef.current = new ResizeObserver(updatePosition)
            resizeObserverRef.current.observe(statusContainer)
        }

        const handleOrientationChange = () => {
            clearRetry()
            scheduleRetry(() => {
                updatePosition()
                setupResizeObserver()
            }, 150)
        }

        // Initial setup with RAF to ensure DOM is ready
        const rafId = requestAnimationFrame(() => {
            updatePosition()
            setupResizeObserver()
        })

        window.addEventListener('resize', updatePosition)

        // Use Screen Orientation API (modern approach) with fallback to deprecated orientationchange
        if('orientation' in screen && 'addEventListener' in screen.orientation) {
            screen.orientation.addEventListener('change', handleOrientationChange)
        } else {
            // Fallback for older browsers that don't support Screen Orientation API
            window.addEventListener('orientationchange', handleOrientationChange)
        }

        return () => {
            cancelAnimationFrame(rafId)
            clearRetry()
            resizeObserverRef.current?.disconnect()
            window.removeEventListener('resize', updatePosition)

            // Clean up orientation listener based on which one was used
            if('orientation' in screen && 'addEventListener' in screen.orientation) {
                screen.orientation.removeEventListener('change', handleOrientationChange)
            } else {
                window.removeEventListener('orientationchange', handleOrientationChange)
            }
        }
    }, [setPlayerInformationContainerPosition, isUserOnTablet])
}
